<template>
  <div>
    <v-card elevation="0" class="px-8">
      <v-row dense align="center" justify="space-between">
        <v-col cols="12" sm="6">
          <h1 class="fs-16 primary--text font-weight-bold">
            {{ title }}
          </h1>
        </v-col>
        <v-col cols="12" sm="auto">
          <v-breadcrumbs class="app-breadcrumbs py-3" :items="breadcrumbs" />
        </v-col>
      </v-row>
    </v-card>
    <div v-if="loading" class="text-center py-12">
      <v-progress-circular size="50" color="primary" indeterminate />
    </div>
    <div v-else-if="notAccessToCreate" class="text-center py-12">
      <h1>OOPS!</h1>
      <p>Sorry, you don't have any permission to access this page</p>
      <v-btn color="primary" to="/sshcp/request" class="btn-medium">
        Back to Request List
      </v-btn>
    </div>
    <v-container v-else class="px-7" fluid>
      <v-row no-gutters align="center" class="mb-3">
        <v-col cols="auto" v-if="id">
          <v-btn to="/sshcp/request" class="mr-2" color="bluedarken" exact icon>
            <v-icon class="icon-bold">mdi-arrow-left</v-icon>
          </v-btn>
        </v-col>
        <v-col cols="12" sm="auto">
          <h1 class="fs-20 font-weight-bold bluedarken--text">
            <template v-if="isRevise || hasRevision">
              <template v-if="hasRevision && !secondRevision">
                {{ detail.unique_code }}
                <span class="menuActive--text">
                  Revision For {{ unique_code_reference }}
                </span>
              </template>
              <template v-else>
                <span class="menuActive--text">
                  Revision For {{ detail.unique_code }}
                </span>
              </template>
            </template>
            <template v-else>
              {{ subtitle }}
            </template>
          </h1>
        </v-col>
        <v-col class="d-flex justify-end">
          <v-btn
            v-if="id && formRequest.status === 'submitted'"
            class="btn-medium"
            depressed
            color="menuActive"
            dark
            @click.prevent="viewRequestSummary"
          >
            View Request Summary
          </v-btn>
          <v-btn
            v-if="canRevise"
            type="button"
            class="btn-medium"
            @click="revise"
          >
            {{ isRevise ? 'Cancel' : 'Revise' }}
          </v-btn>
        </v-col>
      </v-row>

      <v-alert
        :value="isEditing && isNotOwner"
        type="error"
        class="fs-14 mt-4 white--text"
        color="warning"
      >
        You are not authorized to edit this draft
      </v-alert>

      <v-alert
        color="red"
        rounded
        class="fs-12 py-2 mt-3 mb-3 white--text"
        v-if="
          id && formRequest.notes && formRequest.local_status === 'rejected'
        "
      >
        <v-row align="center" justify="space-between" no-gutters>
          <v-col cols="auto" class="font-weight-bold">
            Your Request Has Been Rejected. Reason:
          </v-col>
          <v-col cols="auto">
            <v-btn @click="showReason = !showReason" color="white" icon>
              <v-icon>mdi-chevron-{{ showReason ? 'up' : 'down' }}</v-icon>
            </v-btn>
          </v-col>
        </v-row>
      </v-alert>
      <v-slide-y-transition>
        <div v-if="showReason">
          <div class="px-4 py-4 fs-12">
            {{ formRequest.notes }}
          </div>
          <v-divider />
        </div>
      </v-slide-y-transition>

      <ValidationObserver ref="form" v-slot="{ handleSubmit, invalid }">
        <form
          novalidate
          autocomplete="off"
          @submit.prevent="handleSubmit(onSubmit)"
        >
          <v-card class="mb-4 rounded-10 elevation-card">
            <v-card-title
              class="font-weight-700 fs-14 lh-14 primary pa-4 white--text"
            >
              Requester
            </v-card-title>
            <v-card-text class="black--text pt-4 pb-0">
              <v-row dense align="center">
                <v-col
                  offset="0"
                  offset-sm="2"
                  offset-md="3"
                  cols="12"
                  sm="8"
                  md="6"
                >
                  <label
                    class="mb-1 d-block fs-12 font-weight-regular"
                    for="region"
                  >
                    Region
                  </label>
                  <ValidationProvider
                    slim
                    name="Region"
                    :rules="rules.region"
                    v-slot="{ errors }"
                  >
                    <v-autocomplete
                      id="region"
                      placeholder="Input Region"
                      single-line
                      dense
                      outlined
                      :error-messages="errors"
                      v-model="form.requestor.region"
                      autocomplete="none"
                      :items="regionsMapping"
                      item-value="name"
                      item-text="name"
                      filled
                      readonly
                    ></v-autocomplete>
                  </ValidationProvider>
                  <ValidationProvider
                    slim
                    name="District"
                    :rules="rules.district"
                    v-slot="{ errors }"
                  >
                    <label
                      for="district"
                      class="mb-1 fs-12 font-weight-regular"
                    >
                      District
                    </label>
                    <img
                      v-if="loadingDistricts"
                      src="@/assets/images/loading-bar.gif"
                      class="pl-1"
                      style="height: 22px;position: absolute;"
                      alt="loader"
                    />
                    <v-autocomplete
                      id="district"
                      placeholder="Input District"
                      single-line
                      dense
                      outlined
                      :error-messages="errors"
                      v-model="form.requestor.district"
                      autocomplete="off"
                      filled
                      readonly
                      :items="districts"
                      item-value="name"
                      item-text="name"
                    ></v-autocomplete>
                  </ValidationProvider>
                  <ValidationProvider
                    slim
                    name="District Manager"
                    :rules="rules.district_manager"
                    v-slot="{ errors }"
                  >
                    <label
                      for="district_manager"
                      class="mb-1 fs-12 font-weight-regular"
                      >District Manager</label
                    >
                    <img
                      v-if="loadingDistrictManager"
                      src="@/assets/images/loading-bar.gif"
                      class="pl-1"
                      style="height: 22px;position: absolute;"
                      alt="loader"
                    />
                    <v-text-field
                      id="district_manager"
                      placeholder="District Manager"
                      single-line
                      dense
                      readonly
                      filled
                      outlined
                      :error-messages="errors"
                      v-model="form.requestor.district_manager"
                      :hide-details="!!form.requestor.email_fom"
                    ></v-text-field>
                    <div class="mb-4 fs-12 info--text">
                      {{ form.requestor.email_fom }}
                    </div>
                  </ValidationProvider>
                </v-col>
              </v-row>
            </v-card-text>
          </v-card>
          <v-card class="mb-4 rounded-10 elevation-card">
            <v-card-title
              class="font-weight-700 fs-14 lh-14 primary pa-4 white--text"
            >
              General Information
            </v-card-title>
            <v-card-text class="black--text pt-4 pb-0">
              <v-row align="center">
                <v-col
                  offset="0"
                  offset-sm="2"
                  offset-md="3"
                  cols="12"
                  sm="8"
                  md="6"
                >
                  <v-menu
                    :close-on-content-click="false"
                    transition="scale-transition"
                    nudge-bottom="-10"
                    min-width="290px"
                    v-model="menu"
                    offset-y
                  >
                    <template v-slot:activator="{ on }">
                      <ValidationProvider
                        slim
                        name="Created Date"
                        :rules="rules.createdDate"
                        v-slot="{ errors }"
                      >
                        <label
                          for="createdDate"
                          class="mb-1 d-block fs-12 font-weight-regular"
                        >
                          Created Date
                        </label>
                        <v-text-field
                          id="createdDate"
                          placeholder="Created Date"
                          append-icon="event"
                          single-line
                          dense
                          outlined
                          readonly
                          v-on="on"
                          :error-messages="errors"
                          v-model="form.general_information.created_date"
                          :disabled="isDisabled"
                          :filled="isDisabled"
                        ></v-text-field>
                      </ValidationProvider>
                    </template>
                    <v-date-picker
                      v-model="form.general_information.created_date"
                      :min="minDate"
                      @input="menu = false"
                      no-title
                    >
                    </v-date-picker>
                  </v-menu>

                  <ValidationProvider
                    slim
                    name="Type of Activity"
                    :rules="rules.type_of_activity"
                    v-slot="{ errors }"
                  >
                    <label
                      for="type_of_activity"
                      class="mb-1 d-block fs-12 font-weight-regular"
                      >Type of Activity</label
                    >
                    <v-autocomplete
                      id="type_of_activity"
                      placeholder="Input Type of Activity"
                      single-line
                      dense
                      outlined
                      :error-messages="errors"
                      v-model="form.general_information.type_of_activity"
                      autocomplete="off"
                      :items="activities"
                      item-value="name"
                      item-text="name"
                      :disabled="isDisabled"
                      :filled="isDisabled"
                      @change="onActivityChange"
                    ></v-autocomplete>
                  </ValidationProvider>

                  <ValidationProvider
                    slim
                    name="Budget Year"
                    v-slot="{ errors }"
                    rules="required"
                  >
                    <label
                      class="mb-1 d-block fs-12 font-weight-regular"
                      for="year"
                    >
                      Budget Year
                    </label>
                    <v-autocomplete
                      :items="years"
                      single-line
                      dense
                      placeholder="Input Budget Year"
                      append-icon="mdi-calendar"
                      outlined
                      :error-messages="errors"
                      v-model="form.general_information.year"
                      @change="yearChanged"
                      :disabled="isDisabled"
                      :filled="isDisabled"
                    ></v-autocomplete>
                    <!-- <date-picker
                      id="year"
                      class="mb-5"
                      single-line
                      dense
                      outlined
                      type="year"
                    ></date-picker> -->
                  </ValidationProvider>

                  <ValidationProvider
                    slim
                    name="PO Number"
                    v-slot="{ errors }"
                    :rules="rules.poNumber"
                  >
                    <label
                      class="mb-1 fs-12 font-weight-regular"
                      for="PONumber"
                    >
                      PO Number
                    </label>
                    <img
                      v-if="loadingBudget"
                      src="@/assets/images/loading-bar.gif"
                      class="pl-1"
                      style="height: 22px;position: absolute;"
                      alt="loader"
                    />
                    <v-autocomplete
                      id="PONumber"
                      placeholder="Input PO Number"
                      single-line
                      dense
                      outlined
                      :error-messages="errors"
                      v-model="form.general_information.po_number"
                      @change="poNumberChanged()"
                      :items="po_numbers"
                      item-text="po_number"
                      item-value="po_number"
                      :disabled="isDisabled"
                      :filled="isDisabled"
                      :hide-details="totalBudgetPO !== null"
                    ></v-autocomplete>
                    <div class="mb-0 fs-12" v-if="totalBudgetPO">
                      Total Budget:
                      <span class="info--text">
                        {{ idr(totalBudgetPO.poAmount) }}
                      </span>
                    </div>
                    <div class="mb-0 fs-12" v-if="totalBudgetPO">
                      Draft:
                      <span class="info--text">
                        {{ idr(totalBudgetPO.draft) }}
                      </span>
                    </div>
                    <div class="mb-4 fs-12" v-if="totalBudgetPO">
                      Submitted:
                      <span class="info--text">
                        {{ idr(totalBudgetPO.submitted) }}
                      </span>
                    </div>
                  </ValidationProvider>

                  <ValidationProvider
                    slim
                    name="IO Number"
                    rules="required"
                    v-slot="{ errors }"
                  >
                    <label
                      class="mb-1 fs-12 font-weight-regular"
                      for="io_number"
                    >
                      IO Number
                    </label>
                    <img
                      v-if="loadingIONumber"
                      src="@/assets/images/loading-bar.gif"
                      class="pl-1"
                      style="height: 22px;position: absolute;"
                      alt="loader"
                    />

                    <v-autocomplete
                      id="io_number"
                      placeholder="IO Number"
                      single-line
                      dense
                      outlined
                      :error-messages="errors"
                      v-model="form.general_information.io_number"
                      :disabled="io_numbers.length < 1 || isDisabled"
                      :filled="io_numbers.length < 1 || isDisabled"
                      :items="io_numbers"
                      item-text="io_number"
                      item-value="io_number"
                      :hide-details="form.general_information.io_desc !== ''"
                    ></v-autocomplete>
                    <div
                      class="mb-0 fs-12"
                      v-if="form.general_information.io_desc"
                    >
                      IO Description:
                      <span class="info--text">
                        {{ form.general_information.io_desc }}
                      </span>
                    </div>
                    <div
                      class="mb-4 fs-12"
                      v-if="form.general_information.po_item"
                    >
                      PO Line Item:
                      <span class="info--text">
                        {{ form.general_information.po_item }}
                      </span>
                    </div>
                  </ValidationProvider>

                  <ValidationProvider
                    slim
                    name="GL Account"
                    v-slot="{ errors }"
                  >
                    <label
                      class="mb-1 fs-12 font-weight-regular"
                      for="gl_account"
                    >
                      GL Account
                    </label>
                    <img
                      v-if="loadingGLAccount"
                      src="@/assets/images/loading-bar.gif"
                      class="pl-1"
                      style="height: 22px;position: absolute;"
                      alt="loader"
                    />
                    <v-text-field
                      id="gl_account"
                      placeholder="GL Account"
                      single-line
                      dense
                      outlined
                      disabled
                      filled
                      :error-messages="errors"
                      v-model="form.general_information.gl_account"
                    ></v-text-field>
                  </ValidationProvider>

                  <ValidationProvider
                    slim
                    name="Cost Center"
                    v-slot="{ errors }"
                  >
                    <label
                      class="mb-1 fs-12 font-weight-regular"
                      for="cost_center"
                    >
                      Cost Center
                    </label>
                    <img
                      v-if="loadingGLAccount"
                      src="@/assets/images/loading-bar.gif"
                      class="pl-1"
                      style="height: 22px;position: absolute;"
                      alt="loader"
                    />
                    <v-text-field
                      id="cost_center"
                      placeholder="Cost Center"
                      single-line
                      dense
                      outlined
                      disabled
                      filled
                      :error-messages="errors"
                      v-model="form.general_information.cost_center"
                    ></v-text-field>
                  </ValidationProvider>

                  <ValidationProvider
                    slim
                    name="Total Budget"
                    :rules="{
                      required: true,
                      total_budget: {
                        target: totalBudgetPO
                      }
                    }"
                    v-slot="{ errors }"
                  >
                    <label
                      class="mb-1 d-block fs-12 font-weight-regular"
                      for="totalBudget"
                    >
                      Total Budget
                    </label>
                    <!-- :class="!isEditing ? 'mb-2' : ''" -->
                    <v-currency-field
                      id="totalBudget"
                      placeholder="Total Budget"
                      single-line
                      v-model="form.general_information.total_budget"
                      :error-messages="errors"
                      :disabled="isDisabled"
                      :filled="isDisabled"
                    />
                    <!-- :hideDetails="!isEditing" -->
                  </ValidationProvider>

                  <ValidationProvider
                    slim
                    :rules="rules.total_participant"
                    name="Total Participant"
                    v-slot="{ errors }"
                  >
                    <label
                      class="mb-1 d-block fs-12 font-weight-regular"
                      for="totalParticipant"
                    >
                      Total Participant
                    </label>
                    <v-currency-field
                      id="totalParticipant"
                      placeholder="Total Participant"
                      single-line
                      v-model="form.general_information.total_participant"
                      :error-messages="errors"
                      :disabled="isDisabled"
                      :filled="isDisabled"
                    />
                  </ValidationProvider>

                  <ValidationProvider
                    slim
                    name="Cost per Pax"
                    :rules="{
                      cost_per_pax: {
                        activity: type_of_activity
                      }
                    }"
                    v-slot="{ errors }"
                  >
                    <label
                      class="mb-1 d-block fs-12 font-weight-regular"
                      for="cost_per_pax"
                    >
                      Cost per Pax
                    </label>
                    <!-- :class="!isEditing ? 'mb-2' : ''" -->
                    <v-currency-field
                      id="cost_per_pax"
                      placeholder="Cost per Pax"
                      single-line
                      :value="cost_per_pax"
                      :error-messages="errors"
                      filled
                      :disabled="isDisabled"
                      readonly
                    />
                    <!-- :hideDetails="!isEditing" -->
                  </ValidationProvider>
                </v-col>
              </v-row>
            </v-card-text>
          </v-card>
          <v-card class="mb-4 rounded-10 elevation-card">
            <v-card-title
              class="font-weight-700 fs-14 lh-14 primary pa-4 white--text"
            >
              Supporting Documents
            </v-card-title>
            <v-card-text class="black--text pt-4 pb-0">
              <v-row dense align="center">
                <v-col
                  offset="0"
                  offset-sm="2"
                  offset-md="3"
                  cols="12"
                  sm="8"
                  md="6"
                  v-if="type_of_activity.support_documents.length > 0"
                >
                  <template v-for="(docs, idxDocs) in temp_documents">
                    <ValidationProvider
                      v-if="docs.required"
                      :key="'docs' + idxDocs"
                      slim
                      :name="docs.name"
                      :rules="
                        (docs.s3_url || docs.file) && !isDisabled
                          ? ''
                          : 'required'
                      "
                      v-slot="{ errors }"
                    >
                      <label
                        :for="docs.title"
                        class="fs-12 font-weight-regular mb-1"
                      >
                        {{ docs.name }}
                      </label>
                      <!-- <supporting-document-link
                        v-if="detail && isDisabled"
                        :detail="docs"
                        :name="docs.title"
                        :sshcp="true"
                      /> -->
                      <v-file-input
                        v-if="!isDisabled"
                        :id="docs.title"
                        :placeholder="docs.name"
                        single-line
                        prepend-icon=""
                        append-icon="mdi-attachment"
                        dense
                        outlined
                        :error-messages="errors"
                        v-model="docs.file"
                        :disabled="isDisabled"
                        :filled="isDisabled"
                      ></v-file-input>
                      <div class="mb-6" v-if="detail && !docs.file">
                        <supporting-document-link
                          :detail="docs"
                          :name="docs.file_name_origin"
                          :sshcp="true"
                          small
                        />
                      </div>
                    </ValidationProvider>
                  </template>
                </v-col>
                <v-col cols="12" v-else class="text-center pb-6">
                  <span class="red--text">
                    Please select Type of Activity first!
                  </span>
                </v-col>
              </v-row>
            </v-card-text>
          </v-card>
          <v-card class="mb-4 rounded-10 elevation-card">
            <v-card-title
              class="font-weight-700 fs-14 lh-14 primary pa-4 white--text"
            >
              Event Information
            </v-card-title>
            <v-card-text class="black--text pt-4">
              <v-row>
                <v-col offset="0" offset-sm="3" cols="6">
                  <ValidationProvider
                    ref="totalAmount"
                    slim
                    name="Total Amount"
                    :rules="{
                      required: true,
                      total_amount_event: {
                        target: form.general_information.total_budget
                      }
                    }"
                    v-slot="{ errors }"
                  >
                    <label
                      for="total_amount_event"
                      class="fs-12 font-weight-regular mb-1"
                    >
                      Total Amount
                    </label>
                    <v-currency-field
                      id="total_amount_event"
                      placeholder="Total Amount"
                      v-model="form.event_information.total_amount"
                      single-line
                      readonly
                      :error-messages="errors"
                      filled
                    ></v-currency-field>
                    <!-- <div class="error--text fs-12" v-if="errors.length">
                      {{ errors[0] }}
                    </div> -->
                  </ValidationProvider>
                </v-col>
              </v-row>
              <v-row no-gutters align="center" justify="space-between">
                <v-col cols="10">
                  <v-tabs hide-slider v-model="tab" class="tab-events">
                    <v-tab v-for="(item, index) in events" :key="index">
                      <span class="text-capitalize">Event {{ index + 1 }}</span>
                      <v-btn
                        v-if="events.length > 1 && !isDisabled"
                        class="ml-2"
                        :color="tab === index ? 'error' : ''"
                        icon
                        @click="removeEvent(index)"
                      >
                        <v-icon>mdi-delete</v-icon>
                      </v-btn>
                    </v-tab>
                  </v-tabs>
                </v-col>
                <v-col cols="auto">
                  <v-btn
                    v-if="!isDisabled"
                    color="success"
                    fab
                    right
                    rounded
                    x-small
                    depressed
                    @click="addEvent"
                  >
                    <v-icon>mdi-plus</v-icon>
                  </v-btn>
                </v-col>
              </v-row>
              <v-divider class="primary" style="height:2px;" />

              <v-tabs-items v-model="tab">
                <v-tab-item
                  v-for="(event, eventIndex) in events"
                  :key="eventIndex"
                  @input="onEventInput(event)"
                >
                  <v-card flat>
                    <v-card-text class="black--text pt-4 pb-0">
                      <v-row>
                        <v-col offset="0" offset-sm="3" cols="12" sm="6">
                          <ValidationProvider
                            slim
                            name="Name of Event"
                            rules="required"
                            v-slot="{ errors }"
                          >
                            <label
                              :for="`nameOfEvent${eventIndex}`"
                              class="fs-12 font-weight-regular mb-1"
                            >
                              Name of Event
                            </label>
                            <v-text-field
                              :id="`nameOfEvent${eventIndex}`"
                              placeholder="Name of Event"
                              single-line
                              v-model="event.name"
                              :disabled="isDisabled"
                              :filled="isDisabled"
                              autocomplete="none"
                              dense
                              outlined
                              :error-messages="errors"
                            ></v-text-field>
                          </ValidationProvider>

                          <v-row>
                            <v-col class="py-0">
                              <v-menu
                                :close-on-content-click="false"
                                transition="scale-transition"
                                nudge-bottom="-10"
                                min-width="290px"
                                v-model="event.menuStartDate"
                                offset-y
                              >
                                <template v-slot:activator="{ on }">
                                  <ValidationProvider
                                    slim
                                    name="Start Date"
                                    rules="required"
                                    v-slot="{ errors }"
                                  >
                                    <label
                                      :for="`startDate${eventIndex}`"
                                      class="mb-1 d-block fs-12 font-weight-regular"
                                    >
                                      Start Date
                                    </label>
                                    <v-text-field
                                      :id="`startDate${eventIndex}`"
                                      placeholder="Start Date"
                                      append-icon="event"
                                      single-line
                                      dense
                                      outlined
                                      readonly
                                      v-on="on"
                                      :error-messages="errors"
                                      v-model="event.start_date"
                                      :disabled="isDisabled"
                                      :filled="isDisabled"
                                    ></v-text-field>
                                  </ValidationProvider>
                                </template>
                                <v-date-picker
                                  v-model="event.start_date"
                                  :min="minDate"
                                  @input="event.menuStartDate = false"
                                  no-title
                                >
                                </v-date-picker>
                              </v-menu>
                            </v-col>
                            <v-col class="py-0">
                              <v-menu
                                :close-on-content-click="false"
                                transition="scale-transition"
                                nudge-bottom="-10"
                                min-width="290px"
                                v-model="event.menuEndDate"
                                offset-y
                              >
                                <template v-slot:activator="{ on }">
                                  <ValidationProvider
                                    slim
                                    name="End Date"
                                    rules="required"
                                    v-slot="{ errors }"
                                  >
                                    <label
                                      :for="`endDate${eventIndex}`"
                                      class="fs-12 font-weight-regular mb-1"
                                    >
                                      End Date
                                    </label>
                                    <v-text-field
                                      :id="`endDate${eventIndex}`"
                                      placeholder="End Date"
                                      append-icon="event"
                                      single-line
                                      dense
                                      outlined
                                      readonly
                                      v-on="on"
                                      :error-messages="errors"
                                      v-model="event.end_date"
                                      :disabled="isDisabled"
                                      :filled="isDisabled"
                                    ></v-text-field>
                                  </ValidationProvider>
                                </template>
                                <v-date-picker
                                  v-model="event.end_date"
                                  :min="event.start_date"
                                  @input="event.menuEndDate = false"
                                  no-title
                                >
                                </v-date-picker>
                              </v-menu>
                            </v-col>
                          </v-row>

                          <ValidationProvider
                            slim
                            name="Venue of Event"
                            rules="required"
                            v-slot="{ errors }"
                          >
                            <label
                              :for="`venueOfEvent${eventIndex}`"
                              class="fs-12 font-weight-regular mb-1"
                            >
                              Venue of Event
                            </label>
                            <v-text-field
                              :id="`venueOfEvent${eventIndex}`"
                              placeholder="Venue of Event"
                              single-line
                              v-model="event.venue_of_event"
                              :disabled="isDisabled"
                              :filled="isDisabled"
                              dense
                              outlined
                              :error-messages="errors"
                            ></v-text-field>
                          </ValidationProvider>

                          <ValidationProvider
                            slim
                            name="Activities of Event"
                            rules="required"
                            v-slot="{ errors }"
                          >
                            <label
                              :for="`actOfEvent${eventIndex}`"
                              class="fs-12 font-weight-regular mb-1"
                            >
                              Activities of Event
                            </label>
                            <v-autocomplete
                              :id="`actOfEvent${eventIndex}`"
                              placeholder="Activities of Event"
                              single-line
                              v-model="event.activities_of_event"
                              :disabled="isDisabled"
                              :filled="isDisabled"
                              dense
                              outlined
                              :error-messages="errors"
                              :items="activities_of_event"
                            ></v-autocomplete>
                          </ValidationProvider>

                          <ValidationProvider
                            slim
                            name="Purpose of Event"
                            rules="required"
                            v-slot="{ errors }"
                          >
                            <label
                              :for="`purposeOfEvent${eventIndex}`"
                              class="fs-12 font-weight-regular mb-1"
                            >
                              Purpose of Event
                            </label>
                            <v-text-field
                              :id="`purposeOfEvent${eventIndex}`"
                              placeholder="Purpose of Event"
                              single-line
                              v-model="event.purpose_of_event"
                              dense
                              outlined
                              :error-messages="errors"
                              :disabled="isDisabled"
                              :filled="isDisabled"
                            ></v-text-field>
                          </ValidationProvider>

                          <ValidationProvider
                            slim
                            name="Frequency"
                            rules="required|min_value:1"
                            v-slot="{ errors }"
                          >
                            <label
                              :for="`freq${eventIndex}`"
                              class="fs-12 font-weight-regular mb-1"
                            >
                              Frequency
                            </label>
                            <v-text-field
                              :id="`freq${eventIndex}`"
                              placeholder="Frequency"
                              single-line
                              v-model="event.frequency"
                              :disabled="isDisabled"
                              :filled="isDisabled"
                              type="number"
                              dense
                              outlined
                              :error-messages="errors"
                            ></v-text-field>
                          </ValidationProvider>
                        </v-col>
                      </v-row>

                      <v-simple-table>
                        <template v-slot:default>
                          <thead>
                            <tr>
                              <th class="text-left">Item</th>
                              <th class="text-left">Price</th>
                              <th class="text-left">Quantity</th>
                              <th class="text-left">Frequency</th>
                              <th class="text-left">Amount</th>
                              <th class="text-left"></th>
                              <th class="text-left"></th>
                            </tr>
                          </thead>
                          <tbody>
                            <tr
                              v-for="(item, eventItemIndex) in event.items"
                              :key="eventItemIndex"
                            >
                              <td>
                                <ValidationProvider
                                  slim
                                  name="Item"
                                  rules="required"
                                  v-slot="{ errors }"
                                >
                                  <v-text-field
                                    placeholder="Item"
                                    single-line
                                    v-model="item.item"
                                    :disabled="isDisabled"
                                    :filled="isDisabled"
                                    dense
                                    outlined
                                    hide-details
                                    :error-messages="errors"
                                  ></v-text-field>
                                </ValidationProvider>
                              </td>
                              <td>
                                <ValidationProvider
                                  slim
                                  name="Price"
                                  rules="required|min_value:1"
                                  v-slot="{ errors }"
                                >
                                  <v-currency-field
                                    placeholder="Price"
                                    single-line
                                    v-model="item.price"
                                    :disabled="isDisabled"
                                    :filled="isDisabled"
                                    :error-messages="errors"
                                    hide-details
                                  />
                                </ValidationProvider>
                              </td>
                              <td>
                                <ValidationProvider
                                  slim
                                  name="Quantity"
                                  rules="required|min_value:1"
                                  v-slot="{ errors }"
                                >
                                  <v-text-field
                                    placeholder="Quantity"
                                    single-line
                                    v-model="item.quantity"
                                    :disabled="isDisabled"
                                    :filled="isDisabled"
                                    type="number"
                                    dense
                                    outlined
                                    hide-details
                                    :error-messages="errors"
                                  ></v-text-field>
                                </ValidationProvider>
                              </td>
                              <td>
                                <ValidationProvider
                                  slim
                                  name="Frequency"
                                  rules="required|min_value:1"
                                  v-slot="{ errors }"
                                >
                                  <v-text-field
                                    placeholder="Frequency"
                                    single-line
                                    v-model="item.frequency"
                                    :disabled="isDisabled"
                                    :filled="isDisabled"
                                    type="number"
                                    dense
                                    outlined
                                    hide-details
                                    :error-messages="errors"
                                  ></v-text-field>
                                </ValidationProvider>
                              </td>
                              <td>
                                <ValidationProvider
                                  slim
                                  name="Amount"
                                  rules="required"
                                  v-slot="{ errors }"
                                >
                                  <v-currency-field
                                    placeholder="Amount"
                                    single-line
                                    :value="item.amount"
                                    :error-messages="errors"
                                    hide-details
                                    readonly
                                    filled
                                  />
                                </ValidationProvider>
                              </td>
                              <td class="px-2">
                                <v-btn
                                  v-if="!isDisabled"
                                  outlined
                                  small
                                  icon
                                  color="success"
                                  @click="addEventItem(event, eventItemIndex)"
                                >
                                  <v-icon>mdi-plus</v-icon>
                                </v-btn>
                              </td>
                              <td class="px-2">
                                <v-btn
                                  v-if="event.items.length > 1 && !isDisabled"
                                  outlined
                                  small
                                  color="error"
                                  icon
                                  @click="
                                    removeEventItem(event, eventItemIndex)
                                  "
                                >
                                  <v-icon>mdi-delete</v-icon>
                                </v-btn>
                              </td>
                            </tr>
                          </tbody>
                        </template>
                      </v-simple-table>

                      <v-row>
                        <v-col offset="0" offset-sm="3" cols="6">
                          <ValidationProvider
                            slim
                            name="Total Amount"
                            rules="required"
                            v-slot="{ errors }"
                          >
                            <label
                              for="totalAmountEvent"
                              class="fs-12 font-weight-regular mb-1"
                            >
                              Total Amount
                            </label>
                            <v-currency-field
                              id="totalAmountEvent"
                              placeholder="Total Amount"
                              single-line
                              :error-messages="errors"
                              :value="event.items_total_amount"
                              readonly
                              filled
                            ></v-currency-field>
                          </ValidationProvider>
                        </v-col>
                      </v-row>
                    </v-card-text>
                  </v-card>
                </v-tab-item>
              </v-tabs-items>
            </v-card-text>
          </v-card>
          <v-row no-gutters align="center" class="my-4">
            <v-spacer></v-spacer>
            <v-btn
              class="btn-medium mr-4"
              @click="saveAsDraft"
              :loading="submitting"
              :disabled="isDisabled"
            >
              <v-icon class="mr-2">mdi-content-save</v-icon>
              Save as Draft
            </v-btn>
            <v-btn
              type="submit"
              color="menuActive"
              class="btn-medium"
              :loading="loading || submitting"
              :disabled="invalid || isDisabled || !isTotalAmountIsValid"
              :dark="!invalid && !isDisabled && isTotalAmountIsValid"
            >
              <v-icon class="mr-2">mdi-send</v-icon>
              Submit
            </v-btn>
          </v-row>
        </form>
      </ValidationObserver>
    </v-container>
    <progress-dialog
      :show="progressDialogLoading"
      text="Please wait"
    ></progress-dialog>
  </div>
</template>

<script>
import ProgressDialog from '@/components/progress_dialog';
import { mapState } from 'vuex';
import Swal from 'sweetalert2/dist/sweetalert2.js';
import moment from 'moment';
import { getLocalUser } from '@/utils/helpers';
import SupportingDocumentLink from '@/components/SSHCF/SupportingDocumentLink';
import { yearLists } from '@/utils/helpers';

const createdAt = moment().format('YYYY-MM-DD');

export default {
  name: 'FormRequest',
  components: {
    SupportingDocumentLink,
    'progress-dialog': ProgressDialog
  },
  props: ['id'],
  data: () => ({
    minDate: moment().toISOString(),
    loading: false,
    submitting: false,
    progressDialogLoading: false,
    menu: false,
    loadingDistricts: false,
    loadingDistrictManager: false,
    loadingIONumber: false,
    loadingGLAccount: false,
    districts: [],
    io_numbers: [],
    po_numbers: [],
    type_of_activity: {
      support_documents: []
    },
    detail: null,
    form: {
      requestor: {
        region: '',
        district: '',
        district_manager: '',
        email_fom: ''
      },
      general_information: {
        year: new Date().getFullYear(),
        created_date: createdAt,
        type_of_activity: '',
        po_number: '',
        gl_account: '',
        cost_center: '',
        io_number: '',
        po_item: '',
        io_desc: '',
        total_budget: 0,
        total_participant: 0,
        cost_per_pax: 0
      },
      event_information: {
        events: [],
        total_amount: 0
      },
      supporting_docs: []
    },
    temp_documents: [],
    tab: null,
    events: [
      {
        menuStartDate: false,
        menuEndDate: false,
        purpose_of_event: '',
        activities_of_event: '',
        name: '',
        venue_of_event: '',
        start_date: null,
        end_date: null,
        frequency: 0,
        items_total_amount: 0,
        items: [
          {
            item: '',
            price: 0,
            quantity: 0,
            frequency: 0,
            amount: 0
          }
        ]
      }
    ],
    activities_of_event: [
      {
        text: 'Pelatihan/Training'
      },
      {
        text: 'Pelatihan dan Pemberian Akreditasi/Training and Certification'
      },
      {
        text: 'Seminar/Seminar'
      }
    ],
    statusUploadDocs: [],
    isRevise: false,
    totalBudgetPO: null,
    showReason: false,
    loadingBudget: false,
    unique_code_reference: null,
    secondRevision: false
  }),
  computed: {
    ...mapState('sshcp', ['regionsMapping', 'activities', 'formRequest']),
    years() {
      return yearLists();
    },
    isEditing() {
      return !!this.id && this.formRequest?.status === 'draft';
    },
    title() {
      if (this.isEditing) return 'Edit Form Request';
      return this.id ? 'Detail Form Request' : 'Add New Request';
    },
    subtitle() {
      return this.id
        ? this.formRequest?.unique_code || 'Detail Form Request'
        : 'Form Request';
    },
    isDisabled() {
      const isNotDraft = this.formRequest?.status !== 'draft';
      return (
        (this.isNotOwner && this.isEditing) ||
        (this.id && isNotDraft && !this.isEditing && !this.isRevise)
      );
    },
    notAccessToCreate() {
      return !this.isLoggedInAsFOMSSHCP && !this.isDisabled && !this.isEditing;
    },
    isNotOwner() {
      const user = getLocalUser();
      return this.formRequest?.email_fom !== user?.email;
    },
    breadcrumbs() {
      return [
        {
          text: 'SSHCP',
          to: '/sshcp/'
        },
        {
          text: this.title,
          disabled: true
        }
      ];
    },
    canRevise() {
      return this.id && this.formRequest?.buttonRevise;
    },
    hasRevision() {
      return this.detail?.unique_code_reference;
    },
    rules() {
      const rules = {
        region: 'required',
        district: 'required',
        district_manager: '',
        createdDate: 'required',
        type_of_activity: 'required',
        poNumber: 'required',
        ioNumber: '',
        total_budget: 'required|min_value:1',
        total_participant: 'required|min_value:1',

        supDocProposal: '',
        supDocPermohonan: '',
        supDocPeserta: '',
        supDocInvoice: ''
      };
      return rules;
    },
    cost_per_pax() {
      let total = 0;
      if (
        this.form.general_information.total_participant > 0 &&
        this.form.general_information.total_budget > 0
      ) {
        total = Number(
          this.form.general_information.total_budget /
            this.form.general_information.total_participant
        );
      }
      return total;
    },
    isViewDocsLink() {
      if (!this.detail) return false;

      return this.isEditing;
    },
    isTotalAmountIsValid() {
      let isValid =
        this.form.general_information.total_budget ===
        this.form.event_information.total_amount;
      return isValid;
    }
  },
  watch: {
    'form.requestor.region'(name) {
      if (!name) return;

      const region = this.regionsMapping.find(item => item.name === name);

      if (region) {
        this.fetchDistrictsByRegion(region.id);
      }
    },
    'form.requestor.district'(name) {
      if (!name) return;

      const district = this.districts.find(item => item.name === name);

      if (district) {
        this.form.requestor.district_manager = '';
        this.form.requestor.email_fom = '';
        this.fetchDistrictManager(district.id);
      }
    },
    'form.general_information.po_number'(po_number) {
      if (!po_number) return;

      const poNumber = this.po_numbers.find(
        item => item.po_number === po_number
      );

      if (poNumber) {
        this.fetchIONumber(poNumber.po_number);
        this.getTotalBudgetPO();
      }
    },
    'form.general_information.io_number'(io_number) {
      if (!io_number) return;
      if (io_number) {
        this.fetchGLAccount(io_number);
      }
    },
    cost_per_pax(total) {
      this.form.general_information.cost_per_pax = total;
    }
  },
  async mounted() {
    this.loading = true;
    this.fetchActivities();
    await this.fetchRegionsMapping();
    await this.fetchPONumber();
    this.checkMapping();
    if (this.id) {
      await this.fetchDetail();
    } else {
      this.$store.commit('sshcp/set', {
        formRequest: {}
      });
    }
    this.loading = false;
  },
  methods: {
    revise() {
      this.isRevise = !this.isRevise;
      if (this.isRevise) {
        if (this.hasRevision) {
          this.unique_code_reference = this.formRequest.unique_code;
          this.secondRevision = true;
        } else {
          this.unique_code_reference = this.formRequest.unique_code;
        }
      } else {
        if (this.hasRevision) {
          this.unique_code_reference = this.formRequest.unique_code_reference;
          this.secondRevision = false;
        } else {
          this.unique_code_reference = this.formRequest.unique_code_reference;
        }
      }
    },
    async checkMapping() {
      // get data mapping
      const mapping = JSON.parse(localStorage.getItem('sshcp_mapping'));
      // filter mapping null
      const regions = mapping
        ?.map(item => item.region_mapping_name)
        .filter(val => val !== null);
      const districts = mapping
        ?.map(item => item.district_mapping_name)
        .filter(val => val !== null);

      // set region field
      this.form.requestor.region = regions[0] || '';
      const region = this.regionsMapping.find(item => item.name === regions[0]);
      if (region) {
        await this.fetchDistrictsByRegion(region.id);
      }
      this.form.requestor.district = districts[0] || '';
    },
    yearChanged() {
      this.form.general_information.po_number = '';
      this.poNumberChanged();
      this.fetchPONumber();
    },
    poNumberChanged() {
      this.form.general_information.io_number = '';
      this.form.general_information.gl_account = '';
      this.form.general_information.cost_center = '';
      this.form.general_information.po_item = '';
      this.form.general_information.io_desc = '';
      this.totalBudgetPO = null;
    },
    async fetchDetail() {
      this.loading = true;
      const res = await this.$store.dispatch(
        'sshcp/getFormRequestDetail',
        this.id
      );
      if (res.code === 200) {
        this.loading = false;
        const data = res.data.formRequest;
        this.detail = data;
        this.unique_code_reference = data.unique_code_reference;
        this.form.requestor = {
          region: this.formRequest.region,
          district: this.formRequest.district,
          district_manager: this.formRequest.district_manager,
          email_fom: this.formRequest.email_fom
        };
        this.form.general_information = {
          created_date: moment(this.formRequest.created_date).format(
            'YYYY-MM-DD'
          ),
          year: this.formRequest.year,
          type_of_activity: this.formRequest.type_of_activity,
          po_number: this.formRequest.po_number,
          gl_account: this.formRequest.gl_account,
          cost_center: this.formRequest.cost_center,
          io_number: this.formRequest.io_number,
          total_budget: this.formRequest.total_budget,
          total_participant: this.formRequest.total_participant,
          cost_per_pax: this.formRequest.cost_per_pax
        };
        this.type_of_activity = this.activities.find(
          item => item.name === this.formRequest.type_of_activity
        );
        this.form.supporting_docs = this.formRequest.supporting_docs;
        this.temp_documents = this.formRequest.supporting_docs.map(item => {
          return {
            ...item,
            title: item.type_doc,
            required: true
          };
        });
        let events = {
          menuStartDate: false,
          menuEndDate: false,
          purpose_of_event: '',
          activities_of_event: '',
          name: '',
          venue_of_event: '',
          start_date: null,
          end_date: null,
          frequency: 0,
          items_total_amount: 0,
          items: [
            {
              item: '',
              price: 0,
              quantity: 0,
              frequency: 0,
              amount: 0
            }
          ]
        };
        if (this.formRequest.events.length > 0) {
          this.events = this.formRequest.events;
        } else {
          this.formRequest.events.push(events);
        }
        for (let i = 0; i < this.events.length; i++) {
          this.events[i].start_date = moment(this.events[i].start_date).format(
            'YYYY-MM-DD'
          );
          this.events[i].end_date = moment(this.events[i].end_date).format(
            'YYYY-MM-DD'
          );
          this.onEventInput(this.events[i]);
        }
      } else {
        this.loading = false;
        Swal.fire('Failed!', res.message.text_en, 'error');
      }
    },
    fetchActivities() {
      return this.$store.dispatch('sshcp/getActivities', {
        paginate: false
      });
    },
    fetchRegionsMapping() {
      return this.$store.dispatch('sshcp/getRegionsMapping', {
        paginate: false
      });
    },
    fetchDistrictsByRegion(id) {
      this.loadingDistricts = true;
      return this.$store
        .dispatch('sshcp/getDistrictByRegion', {
          paginate: false,
          id: id
        })
        .then(data => {
          this.loadingDistricts = false;
          if (data.code === 200) {
            this.districts = data.data.districtMappings;
          } else {
            this.loadingDistricts = false;
          }
        })
        .catch(err => {
          console.log(err);
          this.loadingDistricts = false;
        });
    },
    fetchDistrictManager(id) {
      this.loadingDistrictManager = true;
      return this.$store
        .dispatch('sshcp/getDistrictManager', {
          district_mapping_id: id
        })
        .then(data => {
          this.loadingDistrictManager = false;
          if (data.code === 200) {
            this.form.requestor.district_manager = data.data.result.name;
            this.form.requestor.email_fom = data.data.result.email;
          } else {
            Swal.fire('Failed!', data.message.text_en, 'error');
            this.loadingDistrictManager = false;
          }
        })
        .catch(err => {
          Swal.fire('Failed!', err.message.text_en, 'error');
          this.loadingDistrictManager = false;
        });
    },
    fetchIONumber(po_number) {
      this.loadingIONumber = true;
      return this.$store
        .dispatch('sshcp/getIObyPO', {
          po_number: po_number
        })
        .then(data => {
          this.loadingIONumber = false;
          if (data.code === 200) {
            this.io_numbers = data.data.newArrayIO;
          } else {
            Swal.fire('Failed!', data.message.text_en, 'error');
            this.loadingIONumber = false;
          }
        })
        .catch(err => {
          Swal.fire('Failed!', err.message.text_en, 'error');
          this.loadingIONumber = false;
        });
    },
    fetchGLAccount(io_number) {
      this.loadingGLAccount = true;
      return this.$store
        .dispatch('sshcp/getGLAccountbyIO', {
          io_number: io_number,
          po_number: this.form.general_information.po_number
        })
        .then(data => {
          this.loadingGLAccount = false;
          if (data.code === 200) {
            let item = data.data.result;
            this.form.general_information.gl_account = item.glAccount.code;
            this.form.general_information.cost_center = item.costCenter.code;
            this.form.general_information.po_item = item.po_item;
            this.form.general_information.io_desc = item.io_desc;
          }
        })
        .catch(err => {
          Swal.fire('Failed!', err.message.text_en, 'error');
          this.loadingGLAccount = false;
        });
    },
    fetchPONumber() {
      this.loadingBudget = true;
      return this.$store
        .dispatch('sshcp/getBudgetMapping', {
          paginate: false,
          filter: {
            column: 'year',
            data: this.form.general_information.year
          }
        })
        .then(data => {
          this.loadingBudget = false;
          if (data.code === 200) {
            this.po_numbers = data.data;
          } else {
            Swal.fire('Failed!', data.message.text_en, 'error');
          }
        })
        .catch(err => {
          this.loading = false;
          Swal.fire('Failed!', err.message.text_en, 'error');
        });
    },
    getTotalBudgetPO() {
      this.loadingIONumber = true;
      return this.$store
        .dispatch('sshcp/getTotalBudgetPO', {
          po_number: this.form.general_information.po_number
        })
        .then(data => {
          this.loadingIONumber = false;
          if (data.code === 200) {
            this.totalBudgetPO = data.data;
          } else {
            Swal.fire('Failed!', data.message.text_en, 'error');
          }
        })
        .catch(err => {
          this.loadingIONumber = false;
          Swal.fire('Failed!', err.message.text_en, 'error');
        });
    },
    addEvent() {
      this.events.push({
        menuStartDate: false,
        menuEndDate: false,
        purpose_of_event: '',
        activities_of_event: '',
        name: '',
        venue_of_event: '',
        start_date: null,
        end_date: null,
        frequency: 0,
        items_total_amount: 0,
        items: [
          {
            item: '',
            price: 0,
            quantity: 0,
            frequency: 0,
            amount: 0
          }
        ]
      });
      this.tab = this.events.length - 1;
    },
    removeEvent(index) {
      this.events.splice(index, 1);
    },
    addEventItem(event, index) {
      event.items.splice(index + 1, 0, {
        item: '',
        price: 0,
        quantity: 0,
        frequency: 0,
        amount: 0
      });
    },
    removeEventItem(event, index) {
      event.items.splice(index, 1);
      this.onEventInput(event);
    },
    async saveAsDraft() {
      const isValid = await this.$refs.form.validate();
      if (isValid) {
        Swal.fire({
          title: '',
          text: `Are you sure want to ${
            this.id ? 'update' : 'save'
          } this data?`,
          type: 'question',
          showCancelButton: true,
          confirmButtonText: 'Yes',
          cancelButtonText: 'No'
        }).then(result => {
          if (result.value) {
            if ((this.id || this.isEditing) && !this.isRevise) {
              this.updateDraft();
            } else {
              this.submitAsDraft();
            }
          }
        });
      } else {
        const el = document.querySelector(
          '.v-messages.error--text:first-of-type'
        );
        el.scrollIntoView();
        return;
      }
    },
    onSubmit() {
      Swal.fire({
        title: '',
        text: 'Are you sure want to submit this data?',
        type: 'question',
        showCancelButton: true,
        confirmButtonText: 'Yes',
        cancelButtonText: 'No'
      }).then(result => {
        if (result.value) {
          this.publishFormRequest();
          // Swal.fire(
          //   'Success!',
          //   'Form Request has been submitted successfully',
          //   'success'
          // );
        }
      });
    },
    async submitAsDraft() {
      this.submitting = true;
      this.progressDialogLoading = true;
      await this.setupUploadFile();
      // if (this.statusUploadDocs.some(val => val !== 'failed')) {
      let events = this.events;
      if (this.isRevise) {
        events = this.events.map(event => {
          delete event.id;
          return {
            ...event,
            items: event.items.map(item => {
              delete item.id;
              delete item.event_id;
              return {
                ...item
              };
            })
          };
        });
      }
      const res = await this.$store.dispatch('sshcp/saveAsDraft', {
        form: {
          ...this.form,
          unique_code_reference: this.unique_code_reference || null,
          supporting_docs: this.temp_documents,
          event_information: {
            total_amount: this.form.event_information.total_amount,
            events: events
          }
        }
      });
      this.submitting = false;
      this.progressDialogLoading = false;
      this.handleResponse(res);
      // } else {
      //   Swal.fire('Failed!', 'Failed to upload supporting documents!', 'error');
      // }
    },
    async updateDraft() {
      this.submitting = true;
      this.progressDialogLoading = true;
      await this.setupUploadFile();
      // if (this.statusUploadDocs.some(val => val !== 'failed')) {
      const res = await this.$store.dispatch('sshcp/updateFormRequest', {
        form: {
          ...this.form,
          event_information: {
            total_amount: this.form.event_information.total_amount,
            events: this.events
          },
          supporting_docs: this.temp_documents,
          id: this.id,
          unique_code: this.formRequest.unique_code
        }
      });
      this.submitting = false;
      this.progressDialogLoading = false;
      this.handleResponse(res);
      // } else {
      //   Swal.fire('Failed!', 'Failed to upload supporting documents!', 'error');
      // }
    },
    async publishFormRequest() {
      this.submitting = true;
      this.progressDialogLoading = true;
      await this.setupUploadFile();
      // if (this.statusUploadDocs.some(val => val !== 'failed')) {
      let events = this.events;
      if (this.isRevise) {
        events = this.events.map(event => {
          delete event.id;
          return {
            ...event,
            items: event.items.map(item => {
              delete item.id;
              delete item.event_id;
              return {
                ...item
              };
            })
          };
        });
      }
      let form = {
        ...this.form,
        unique_code_reference: this.unique_code_reference,
        amount_submitted: this.totalBudgetPO.submitted,
        amount_draft: this.totalBudgetPO.draft,
        amount_po: this.totalBudgetPO.poAmount,
        supporting_docs: this.temp_documents,
        event_information: {
          total_amount: this.form.event_information.total_amount,
          events: events
        }
      };
      if ((this.isEditing || this.id) && !this.isRevise) {
        form.id = this.id;
        form.unique_code = this.formRequest.unique_code;
      }
      const res = await this.$store.dispatch('sshcp/submitFormRequest', {
        form: form
      });
      this.submitting = false;
      this.progressDialogLoading = false;
      this.handleResponse(res);
      // } else {
      //   Swal.fire('Failed!', 'Failed to upload supporting documents!', 'error');
      // }
    },
    handleResponse(res) {
      if (res.code === 200) {
        Swal.fire('Success!', res.message.text_en, 'success');
        this.$router.push({
          name: 'sshcp_request'
        });
      } else {
        Swal.fire('Failed!', res.message.text_en, 'error');
      }
    },
    async setupUploadFile() {
      for (let i = 0; i < this.temp_documents.length; i++) {
        if (this.temp_documents[i].file) {
          await this.uploadFile(this.temp_documents[i], i);
        }
      }
    },
    async uploadFile(item, index) {
      let formData = new FormData();
      formData.append('docs', item.file);
      await this.$store
        .dispatch('sshcp/uploadSuppDocs', formData)
        .then(data => {
          if (data.code === 200) {
            this.statusUploadDocs.push('success');
            // let alreadyUpload = this.temp_documents.some(
            //   val => item.title === val.type_doc
            // );
            // if (alreadyUpload) {
            this.temp_documents[index] = {
              ...data.data.result,
              type_doc: item.title
            };
            // } else {
            //   this.temp_documents.push({
            //     ...data.data.result,
            //     type_doc: item.title
            //   });
            // }
          } else {
            this.statusUploadDocs.push('failed');
            Swal.fire('Failed!', data.message.text_en, 'error');
          }
        })
        .catch(err => {
          this.statusUploadDocs.push('failed');
          Swal.fire('Failed!', err.response.data.message.text_en, 'error');
        });
    },
    onEventInput(item) {
      item.items.forEach(
        elm => (elm.amount = elm.price * elm.quantity * elm.frequency)
      );
      item.items_total_amount =
        item.frequency * this.sumNumber(item.items, 'amount');
      this.form.event_information.total_amount = this.sumNumber(
        this.events,
        'items_total_amount'
      );
    },
    sumNumber(items, key) {
      if (items) {
        return items.reduce(function(a, b) {
          return a + b[key];
        }, 0);
      }
    },
    viewRequestSummary() {
      this.$router.push(`/sshcp/request/detail/${this.id}`);
    },
    onActivityChange(e) {
      this.type_of_activity = this.activities.find(item => item.name === e);
      let temp_documents = this.copy(this.temp_documents);
      this.temp_documents = this.type_of_activity.support_documents
        .map(item => {
          let currentDocs = null;
          if (!this.id) {
            currentDocs = temp_documents.find(
              docs => docs.title === item.title
            );
          }
          return {
            ...item,
            file: currentDocs?.file || null,
            ...this.detail?.supporting_docs.find(
              docs => docs.type_doc === item.title
            )
          };
        })
        .filter(v => v.required);
    }
  }
};
</script>

<style lang="scss" scoped>
.hoverable-tr {
  cursor: pointer;

  &:hover {
    background: #dfdfdf;
  }
}

.drawer-menu--scroll {
  max-height: 600px;
  overflow: auto;
}
</style>
